import React, { useState } from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
import FileUpload from '../../../../helper/FileUpload';
import uploadIcon from '/src/assets/images/identity/paper-upload.svg?url';

interface DisableIdentityModalProps {
  onClose: () => void;
  onConfirm: (nationalId: string, selectedFile: File) => void;
  isLoading: boolean;
}

const DisableIdentityModal: React.FC<DisableIdentityModalProps> = ({
  onClose,
  onConfirm,
  isLoading,
}) => {
  const [nationalId, setNationalId] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [nationalIdError, setNationalIdError] = useState('');

  // Checked here so a typo is caught before the otp round trip; the api
  // re-validates with the same checksum (App\Domains\Shared\Rules\LegalId).
  const isValidId = /^\d{11}$/.test(nationalId);

  const handleSubmit = () => {
    if (!isValidId) {
      setNationalIdError('شناسه ملی باید ۱۱ رقم باشد');
      return;
    }

    if (selectedFile) {
      onConfirm(nationalId, selectedFile);
    }
  };

  return (
    <Box
      sx={{
        width: '480px',
        maxHeight: '90vh',
        padding: 24,
        background: 'white',
        borderRadius: 2,
        border: '1px #E5E5E8 solid',
        flexDirection: 'column',
        textAlign: 'right',
        gap: 3,
        display: 'flex',
        direction: 'rtl',
      }}
    >
      <Typography
        variant="h6"
        fontWeight="700"
        color="#25282E"
        sx={{ alignSelf: 'stretch', textAlign: 'right' }}
      >
        تغییر هویت به حقوقی
      </Typography>

      <Typography
        variant="body2"
        color="#60646E"
        sx={{ alignSelf: 'stretch', textAlign: 'right', mb: 2 }}
      >
        برای تغییر هویت از حالت حقیقی به حقوقی لطفا اطلاعات زیر را وارد کنید
      </Typography>

      {/* شماره شناسه 11 رقمی */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography
          variant="body2"
          fontWeight="600"
          color="#25282E"
          sx={{ mb: 1, alignSelf: 'flex-start' }}
        >
          شماره شناسه یازده رقمی
        </Typography>
        <TextField
          variant="outlined"
          placeholder="شماره شناسه یازده رقمی را وارد کنید"
          value={nationalId}
          onChange={(e) => {
            // Digits only — the field previously accepted anything and the
            // failure only surfaced after the code was sent.
            setNationalId(e.target.value.replace(/\D/g, ''));
            setNationalIdError('');
          }}
          size="small"
          inputProps={{ maxLength: 11, inputMode: 'numeric' }}
          error={!!nationalIdError}
          helperText={nationalIdError}
          sx={{
            width: '85%',
            '& .MuiOutlinedInput-root': {
              direction: 'rtl',
              fontFamily: 'IRANYekanXFaNum',
            },
          }}
        />
      </Box>

      {/* آپلود روزنامه رسمی */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="body2"
          fontWeight="600"
          color="#25282E"
          sx={{ mb: 2, textAlign: 'right' }}
        >
          تصویر معرفی نامه شرکت با سربرگ رسمی
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ p: 2, backgroundColor: '#F9FAFB', borderRadius: 1, width: '85%' }}>
            <FileUpload
              onFilesSelected={(files) => {
                if (files.length > 0) {
                  setSelectedFile(files[0]);
                  console.log('Contract file selected:', files[0].name);
                }
              }}
              imageSrc={uploadIcon}
              uploadText="آپلود تصویر معرفی نامه شرکت با سربرگ رسمی"
            />
            {selectedFile && (
              <Typography
                variant="caption"
                color="#059669"
                sx={{ mt: 1, display: 'block', textAlign: 'right' }}
              >
                فایل انتخاب شده: {selectedFile.name}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* دکمه‌ها */}
      <Box
        sx={{
          alignSelf: 'stretch',
          justifyContent: 'space-between',
          alignItems: 'center',
          display: 'flex',
          mt: 2,
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !isValidId || !selectedFile}
          sx={{
            backgroundColor: '#2563EB',
            color: 'white',
            padding: '8px 24px',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: '#1E40AF',
            },
            '&:disabled': {
              backgroundColor: '#9CA3AF',
            },
          }}
        >
          <Typography variant="body2" fontWeight="600">
            {isLoading ? 'در حال ارسال...' : 'ثبت تغییرات'}
          </Typography>
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isLoading}
          sx={{
            borderColor: '#9CA3AF',
            color: '#6B7280',
            padding: '8px 24px',
            borderRadius: 1,
            '&:hover': {
              borderColor: '#6B7280',
              backgroundColor: '#F3F4F6',
            },
          }}
        >
          <Typography variant="body2" fontWeight="500">
            انصراف
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

export default DisableIdentityModal;
